import {
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { useFeature } from "src/feature/useFeature";
import { useIsTripScreen } from "src/utils/hooks/useIsTripScreen";
import useSearch from "src/utils/hooks/useSearch";
import { useTripOrTransportIndexes } from "src/utils/hooks/useTripOrTransportIndexes";
import {
  type ReturnStage,
  useTypedLocation,
} from "src/utils/hooks/useTypedLocation";
import { navigateToNewState } from "src/utils/location/navigateToNewState";
import type { BookingDetails, Itinerary } from "../../api/SchedulesResponse";
import type { Mode } from "../../utils/types/mode";
import { useTripSearchResponse } from "../TripPlanner/hooks/useTripSearchResponse";
import { createSegmentScreenViewModel } from "./createSegmentScreenViewModel";
import type { ScheduleItem } from "./DynamicSchedules/createScheduleItemList";
import { useTravelSearchSettings } from "./TravelSearchSettingsProvider";

const defaultReturnFlowState: ReturnFlowStateType = {
  isReturn: false,
};

export type SelectedScheduleType = {
  itinerary?: Itinerary;
  price?: string;
  summary?: ScheduleItem;
  scheduleIndex?: number;
};

export type DualBookingDetails = {
  outboundBookingDetails: BookingDetails;
  inboundBookingDetails: BookingDetails;
};

type RedirectDetailsType = {
  bookingDetails?: {
    singleBookingDetails?: BookingDetails;
    dualBookingDetails?: DualBookingDetails;
  };
};

export type ReturnFlowStateType = {
  isReturn: boolean;
  departingSchedule?: SelectedScheduleType;
  returnSchedule?: SelectedScheduleType;
  redirectDetails?: RedirectDetailsType;
};

type ReturnFlowState = {
  returnFlowState: ReturnFlowStateType;
  setReturnFlowState: Dispatch<SetStateAction<ReturnFlowStateType>>;
  isReturnsSupported: boolean;
};

const ReturnFlowStateContext = createContext<ReturnFlowState>({
  returnFlowState: defaultReturnFlowState,
  setReturnFlowState: () => {},
  isReturnsSupported: false,
});

export function useReturnFlowState() {
  const context = useContext(ReturnFlowStateContext);
  if (!context) {
    throw new Error(
      `useReturnFlowState must be used within ReturnFlowStateProvider`,
    );
  }
  return context;
}

type Props = {
  returnsFlowLocation?: ReturnStage;
};

export function ReturnFlowStateProvider(props: PropsWithChildren<Props>) {
  const isTripsScreen = useIsTripScreen();
  const { tripSearchResponse } = useTripSearchResponse();
  const { searchResponse: searchFromQuery } = useSearch();
  const searchResponse = isTripsScreen ? tripSearchResponse : searchFromQuery;
  const { dateObject } = useTravelSearchSettings();

  // As we have two route canonicals in the URL (map/a/b) and #trips/a/b we need
  // to make sure we check the correct one based on if we're in the trip planner
  const { routeIndex, routeSegmentIndex } = useTripOrTransportIndexes();

  const navigate = useNavigate();
  const location = useTypedLocation();
  const staticConnectedSchedulesFeature = useFeature(
    "UseStaticConnectedSchedules",
  );

  const [returnFlowState, setReturnFlowState] = useState<ReturnFlowStateType>({
    isReturn: false,
  });

  const isReturnsSupported = useMemo(() => {
    if (
      searchResponse === undefined ||
      routeIndex === undefined ||
      searchResponse.routes[routeIndex] === undefined
    ) {
      return false;
    }
    // TODO: We don't need all of this function, just a bit of it. We need to refactor our
    // scheduleResponse usage to be more granular.
    const viewModel = createSegmentScreenViewModel(
      searchResponse,
      routeIndex,
      routeSegmentIndex,
      undefined,
      staticConnectedSchedulesFeature,
      undefined,
    );

    return (
      isReturnsEligibleMode(viewModel.transitMode) &&
      viewModel.capabilities.isBookable
    );
  }, [
    routeIndex,
    routeSegmentIndex,
    searchResponse,
    staticConnectedSchedulesFeature,
  ]);

  const isReturn = !!dateObject.returnDate && isReturnsSupported;

  const providerValue = useMemo(
    () => ({
      returnFlowState: {
        ...returnFlowState,
        isReturn,
      },
      setReturnFlowState,
      isReturnsSupported,
    }),
    [returnFlowState, isReturnsSupported, isReturn],
  );

  // Another hack for now - reset the returnFlowLocation if we're missing selected schedules.
  // Note: We don't need to move forwards - the user can do that if they want
  // TODO: This should be moved into a top-level component handling all schedule issues (e.g. TripSummary and SegmentScreen)
  useEffect(() => {
    // If we expect a departure schedule to be provided, and none is
    if (
      props.returnsFlowLocation &&
      ["return", "summary"].includes(props.returnsFlowLocation) &&
      !returnFlowState.departingSchedule
    ) {
      navigateToNewState(
        navigate,
        { returnsFlowLocation: "departing" },
        location,
        true,
      );
    }
    // If we expect a return schedule to be provided, and none is
    else if (
      props.returnsFlowLocation === "summary" &&
      isReturn &&
      !returnFlowState.returnSchedule
    ) {
      navigateToNewState(
        navigate,
        { returnsFlowLocation: "return" },
        location,
        true,
      );
    }
    // Should only check on render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ReturnFlowStateContext value={providerValue}>
      {props.children}
    </ReturnFlowStateContext>
  );
}

function isReturnsEligibleMode(transitMode: Mode) {
  return (
    transitMode !== "helicopter" &&
    transitMode !== "towncar" &&
    transitMode !== "taxi" &&
    transitMode !== "shuttle" &&
    transitMode !== "walk"
  );
}
